export type StreakPhase = 'habit' | 'commitment' | 'prestige' | 'legendary';

export type StreakBonusTier = {
    minDays: number;
    bonusPct: number;
    phase: StreakPhase;
};

export const STREAK_BONUS_TIERS: readonly StreakBonusTier[] = [
    { minDays: 1, bonusPct: 5, phase: 'habit' },
    { minDays: 3, bonusPct: 10, phase: 'habit' },
    { minDays: 7, bonusPct: 15, phase: 'habit' },
    { minDays: 14, bonusPct: 20, phase: 'habit' },
    { minDays: 21, bonusPct: 25, phase: 'commitment' },
    { minDays: 30, bonusPct: 30, phase: 'commitment' },
    { minDays: 45, bonusPct: 35, phase: 'commitment' },
    { minDays: 60, bonusPct: 40, phase: 'commitment' },
    { minDays: 90, bonusPct: 50, phase: 'prestige' },
    { minDays: 120, bonusPct: 55, phase: 'prestige' },
    { minDays: 180, bonusPct: 65, phase: 'prestige' },
    { minDays: 270, bonusPct: 75, phase: 'prestige' },
    { minDays: 365, bonusPct: 100, phase: 'prestige' },
    { minDays: 500, bonusPct: 110, phase: 'legendary' },
    { minDays: 730, bonusPct: 125, phase: 'legendary' },
    { minDays: 1000, bonusPct: 150, phase: 'legendary' },
    { minDays: 1460, bonusPct: 175, phase: 'legendary' },
    { minDays: 1825, bonusPct: 200, phase: 'legendary' },
] as const;

export function getStreakBonusPct(streakDays: number): number {
    if (streakDays <= 0) return 0;
    let pct = 0;
    for (const tier of STREAK_BONUS_TIERS) {
        if (streakDays >= tier.minDays) pct = tier.bonusPct;
    }
    return pct;
}

export function getStreakPhase(streakDays: number): StreakPhase | null {
    if (streakDays <= 0) return null;
    let phase: StreakPhase = STREAK_BONUS_TIERS[0].phase;
    for (const tier of STREAK_BONUS_TIERS) {
        if (streakDays >= tier.minDays) phase = tier.phase;
    }
    return phase;
}

export type NextStreakTier = {
    daysUntil: number;
    nextPct: number;
    milestoneDay: number;
};

export function getNextStreakTier(streakDays: number): NextStreakTier | null {
    const next = STREAK_BONUS_TIERS.find(t => t.minDays > streakDays);
    if (!next) return null;
    return {
        daysUntil: next.minDays - streakDays,
        nextPct: next.bonusPct,
        milestoneDay: next.minDays,
    };
}

export type StreakTierCrossing = {
    fromPct: number;
    toPct: number;
    milestoneDay: number;
};

export function getStreakTierCrossing(prevStreak: number, nextStreak: number): StreakTierCrossing | null {
    if (nextStreak <= prevStreak) return null;
    const fromPct = getStreakBonusPct(prevStreak);
    const toPct = getStreakBonusPct(nextStreak);
    if (toPct <= fromPct) return null;
    const milestone = STREAK_BONUS_TIERS.find(t => t.minDays > prevStreak && t.minDays <= nextStreak && t.bonusPct === toPct);
    if (!milestone) return null;
    return { fromPct, toPct, milestoneDay: milestone.minDays };
}

export function computeStreakDividendHp(streakDays: number, hpGained: number, hpLost: number): number {
    const profit = hpGained - hpLost;
    if (profit <= 0 || streakDays <= 0) return 0;
    const pct = getStreakBonusPct(streakDays);
    if (pct <= 0) return 0;
    return Math.ceil((profit * pct) / 100);
}

export function formatStreakBonusPct(pct: number): string {
    return pct >= 100 && pct % 100 === 0 ? `×${1 + pct / 100}` : `+${pct}%`;
}
